import { cn } from "@/lib/utils";
import { Eyebrow } from "./SectionHeader";

export interface Stat {
  label: string;
  value: React.ReactNode;
  /** At most one. The highlighted cell gets a wash and a gold rule on top. */
  highlight?: boolean;
}

/**
 * The band of headline figures across the top of a record.
 *
 * It exists so the two or three numbers someone actually opened the page for
 * are readable without reading anything: what it is, when it is, and what it
 * pays. One cell may be highlighted, which is how "Your pay" gets to be the
 * thing your eye lands on first.
 */
export function StatStrip({
  stats,
  className,
}: {
  stats: Stat[];
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 divide-x divide-y divide-border border-y border-border sm:divide-y-0",
        stats.length >= 4 ? "sm:grid-cols-4" : "sm:grid-cols-3",
        className,
      )}
    >
      {stats.map((s) => (
        <div
          key={s.label}
          className={
            s.highlight
              ? "relative bg-primary/[0.07] px-6 py-5 before:absolute before:inset-x-0 before:top-0 before:h-0.5 before:bg-primary"
              : "px-6 py-5"
          }
        >
          <Eyebrow className={s.highlight ? "text-primary/80" : undefined}>
            {s.label}
          </Eyebrow>
          {/*
            Large figures keep the display serif. Bodoni's hairlines are what
            make it unreadable at 15px on a dark ground, but at 24px and above
            the same contrast reads as expensive rather than as thin, so the
            serif is right here and wrong in a list.
          */}
          {/*
            Long values step down a size rather than wrapping. A time range is
            the longest thing that lands in a cell, and at 24px "8:39 AM -
            12:39 PM" broke after the second figure and left an orphaned "PM"
            on its own line, which reads as a layout bug rather than as a time.
            One size down it fits, and a figure at 20px next to one at 24px is
            not a difference anyone notices.
          */}
          <p
            className={cn(
              "mt-1.5 font-display tabular-nums",
              s.highlight
                ? "text-[32px] leading-tight text-primary"
                : "text-foreground",
              !s.highlight &&
                (typeof s.value === "string" && s.value.length > 13
                  ? "text-xl"
                  : "text-2xl"),
            )}
          >
            {s.value}
          </p>
        </div>
      ))}
    </div>
  );
}
